import type { PlanConfigExpanded } from "@brookbush/types";

import type Stripe from "stripe";

import { convertToCents, getTotalAmountDue } from "./helpers";

/**
 * Amount (in cents) to hand Stripe Elements for the Become-a-Member checkout.
 *
 * The selected price can briefly be missing from the price list (flag reload,
 * sign-in) and a plan-change proration refetches on every selection. Elements
 * stays mounted through both, and `elements.update()` throws on a missing or
 * NaN amount, so fall back to the last valid amount instead.
 *
 * @param options.planConfig - Current plan selection with its resolved price
 * @param options.isLoadingPlanConfig - Plan config is still resolving
 * @param options.isChangingPlan - Existing subscriber switching plans
 * @param options.proration - Proration preview for a plan change
 * @param options.previousAmount - Last amount handed to Elements
 * @param options.joiningFee - Joining-price fee in dollars for a new subscription
 * @returns {number | undefined} Non-negative amount in cents, or the previous amount
 */
export const resolveCheckoutElementsAmount = (options: {
	planConfig: PlanConfigExpanded;
	isLoadingPlanConfig: boolean;
	isChangingPlan: boolean;
	proration?: Stripe.Invoice;
	previousAmount?: number;
	joiningFee?: number;
}): number | undefined => {
	const {
		planConfig,
		isLoadingPlanConfig,
		isChangingPlan,
		proration,
		previousAmount,
		joiningFee,
	} = options;

	if (isLoadingPlanConfig) return previousAmount;

	if (isChangingPlan) {
		return Number.isFinite(proration?.total)
			? Math.max(proration!.total, 0)
			: previousAmount;
	}

	if (!planConfig.price) return previousAmount;

	const amount = convertToCents(getTotalAmountDue(planConfig, joiningFee));
	return Number.isFinite(amount) ? amount : previousAmount;
};
